/**
 * Store Module - ProductList Page
 *
 * Grid of product cards with links to individual product detail pages.
 * Data is fetched from GET /v1/products via the useProducts hook.
 */
import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useProducts } from '../hooks/use-products'

export default function ProductList() {
  const { data: products, isLoading, isError, error } = useProducts()
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const productList = useMemo(() => products ?? [], [products])
  const filteredProducts = useMemo(
    () =>
      productList.filter((product) =>
        product.name.toLowerCase().includes(debouncedSearch)
      ),
    [productList, debouncedSearch]
  )

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase())
    }, 250)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [searchInput])

  if (isLoading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-3xl font-bold text-tf-text mb-6">Products</h1>
        <div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          aria-busy="true"
          aria-label="Loading products"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-lg bg-tf-bg-card shadow animate-pulse"
            >
              <div className="h-56 w-full bg-tf-border" />
              <div className="p-4 space-y-2">
                <div className="h-3 w-1/3 rounded bg-tf-border" />
                <div className="h-4 w-2/3 rounded bg-tf-border" />
                <div className="h-3 w-1/4 rounded bg-tf-border" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="px-4 py-6 sm:px-0" role="alert">
        <h1 className="text-3xl font-bold text-tf-text mb-4">Products</h1>
        <p className="text-tf-error">
          {error?.message ?? 'Failed to load products. Please try again.'}
        </p>
      </div>
    )
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-3xl font-bold text-tf-text mb-6">Products</h1>
      <div className="mb-6">
        <label htmlFor="product-search" className="mb-2 block text-sm font-medium text-tf-text-sub">
          Search products
        </label>
        <input
          id="product-search"
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search by product name..."
          className="w-full rounded-lg border border-tf-border bg-tf-bg-card px-4 py-2.5 text-sm text-tf-text placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-1"
        />
      </div>
      {productList.length === 0 ? (
        <p className="text-tf-text-muted">No products available.</p>
      ) : filteredProducts.length === 0 ? (
        <p className="text-tf-text-muted">No results found for "{searchInput.trim()}".</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) => (
            <Link
              key={product.id}
              to={`/store/products/${product.id}`}
              className="group block overflow-hidden rounded-lg bg-tf-bg-card shadow transition-shadow hover:shadow-lg"
            >
              <img
                src={product.imageUrl}
                alt={product.name}
                className="h-56 w-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="p-4">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-tf-text-muted">
                  {product.sku}
                </p>
                <h3 className="mb-2 text-base font-semibold text-tf-text">
                  {product.name}
                </h3>
                <p className="text-sm text-tf-text-sub line-clamp-2">
                  {product.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
